export const ONTIME_PAGE_DEFS = [
  { key: 'overview', label: 'Overview', to: '/link', end: true },
  { key: 'control-map', label: 'Control Map', to: '/link/map' },
  { key: 'control-tower', label: 'Control Tower', to: '/link/tower' },
  { key: 'intake', label: 'Online Intake', to: '/link/intake' },
  { key: 'products', label: 'Products', to: '/link/products' },
  { key: 'store-queue', label: 'Store Queue', to: '/link/store' },
  { key: 'driver-view', label: 'Driver View', to: '/link/driver' },
  { key: 'reports', label: 'Reports', to: '/link/reports' },
  { key: 'customers', label: 'Customers', to: '/link/customers' },
  { key: 'archives', label: 'Archives', to: '/link/archives' },
  { key: 'settings', label: 'Settings', to: '/link/settings' },
  { key: 'profiles', label: 'Profiles', to: '/link/profiles' },
  { key: 'my-view', label: 'My View', to: '/link/my' },
]


export const OWNER_DEFAULT_PAGE_KEYS = ONTIME_PAGE_DEFS.map((page) => page.key)
export const ADMIN_DEFAULT_PAGE_KEYS = [
  'overview',
  'control-map',
  'control-tower',
  'intake',
  'products',
  'customers',
  'archives',
  'reports',
  'settings',
  'my-view',
]
export const ONLINE_DEFAULT_PAGE_KEYS = ['overview', 'control-map', 'control-tower', 'intake', 'archives', 'my-view']
export const STORE_DEFAULT_PAGE_KEYS = ['store-queue', 'archives']
export const DRIVER_DEFAULT_PAGE_KEYS = ['driver-view', 'archives']

const PATH_TO_PAGE_KEY = Object.fromEntries(ONTIME_PAGE_DEFS.map((page) => [page.to, page.key]))

export function emailDocId(email = '') {
  return String(email || '').trim().toLowerCase().replace(/[.#$\[\]/]/g, '_')
}

export function normalizeOnTimeRole(role = '') {
  const value = String(role || '').trim().toLowerCase()
  if (!value) return ''
  if (value in { owner:1, admin:1, store:1, driver:1 }) return value
  if (['online team', 'online_team', 'online-team', 'onlineteam'].includes(value)) return 'online-team'
  if (value.includes('owner')) return 'owner'
  if (value.includes('admin')) return 'admin'
  if (value.includes('online')) return 'online-team'
  if (value.includes('store')) return 'store'
  if (value.includes('driver')) return 'driver'
  return value.replace(/\s+/g, '-').replace(/_/g, '-')
}

export function normalizeModuleClaims(claims) {
  const c = claims || {}
  let apps = []
  if (Array.isArray(c.apps)) apps = c.apps.map((x) => String(x).toLowerCase())
  else if (typeof c.apps === 'string') {
    try {
      const parsed = JSON.parse(c.apps)
      if (Array.isArray(parsed)) apps = parsed.map((x) => String(x).toLowerCase())
    } catch (e) {}
  }
  if (!apps.length && typeof c.apps === 'string') {
    apps = String(c.apps).split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)
  }
  const role = normalizeOnTimeRole(c.onTimeRole || c.role || '')
  const defaultApp = String(c.defaultApp || '').toLowerCase()
  const isSuper = c?.superAdmin === true || c?.superadmin === true
  const orgId = c?.orgId ? String(c.orgId) : ''
  const active = c?.active !== false && c?.disabled !== true && c?.isDisabled !== true && c?.removed !== true && c?.archived !== true
  const canManageOnTimeUsers = c?.canManageOnTimeUsers === true || c?.manageOnTimeUsers === true
  return { ...c, apps, role, defaultApp, isSuper, orgId, active, canManageOnTimeUsers }
}

export function formatOnTimeRoleLabel(role = '') {
  switch (normalizeOnTimeRole(role)) {
    case 'owner': return 'Owner'
    case 'admin': return 'Admin'
    case 'online-team': return 'Online Team'
    case 'store': return 'Store'
    case 'driver': return 'Driver'
    default: return String(role || 'User')
  }
}

function normalizePageKey(value = '') {
  const v = String(value || '').trim().toLowerCase()
  if (!v) return ''
  if (['overview', 'home'].includes(v)) return 'overview'
  if (['controlmap', 'control_map', 'map'].includes(v)) return 'control-map'
  if (['controltower', 'control_tower', 'tower'].includes(v)) return 'control-tower'
  if (['store', 'storequeue', 'store_queue'].includes(v)) return 'store-queue'
  if (['driver', 'driverview', 'driver_view'].includes(v)) return 'driver-view'
  if (['my', 'myview', 'my_view'].includes(v)) return 'my-view'
  return v.replace(/_/g, '-')
}

function cleanPageKeys(values = []) {
  const deduped = Array.from(new Set((Array.isArray(values) ? values : []).map(normalizePageKey).filter(Boolean)))
  return deduped.filter((key) => ONTIME_PAGE_DEFS.some((page) => page.key === key))
}

function expandLinkDashboardKeys(keys = []) {
  const expanded = new Set(keys)
  // Anyone who can access the old Control Tower dashboard must also get the new Link shell pages.
  // This prevents /link and /link/map from being treated as unauthorized and redirected to /link/tower.
  if (expanded.has('control-tower') || expanded.has('overview') || expanded.has('control-map')) {
    expanded.add('overview')
    expanded.add('control-map')
    expanded.add('control-tower')
  }
  return Array.from(expanded)
}

export function getRequestedOnTimePageKey(pathname = '') {
  const rawPath = String(pathname || '').split('?')[0].replace(/\/$/, '') || '/link'
  const path = rawPath.replace(/^\/ontime(?=\/|$)/, '/link')
  return PATH_TO_PAGE_KEY[path] || 'overview'
}

export function hasOnTimeAccess(claims) {
  const c = normalizeModuleClaims(claims)
  if (c.isSuper) return true
  if (!c.orgId || !c.active) return false

  const appAliases = ['ontime', 'on_time', 'link', 'orders', 'neo_orders', 'dispatch']
  const explicitOnTimeRole = normalizeOnTimeRole(c.onTimeRole || '')
  const explicitPages = cleanPageKeys(c.onTimePages || c.ontimePages || c.allowedPages || c.tabsAllowed || c.allowedTabs || c.pageAccess || [])
  const explicitApp = c.apps.some((app) => appAliases.includes(app)) || appAliases.includes(c.defaultApp)

  if (['owner', 'admin', 'online-team', 'store', 'driver'].includes(explicitOnTimeRole)) return true
  if (explicitPages.length > 0) return true
  if (explicitApp) return true
  return false
}

export function getAllowedOnTimePageKeys(claims) {
  const c = normalizeModuleClaims(claims)
  if (!hasOnTimeAccess(c)) return []
  const withArchives = (keys = []) => Array.from(new Set([...expandLinkDashboardKeys(keys), 'archives']))
  const explicit = cleanPageKeys(c.onTimePages || c.ontimePages || c.allowedPages || c.tabsAllowed || c.allowedTabs || c.pageAccess || [])
  if (c.isSuper) return withArchives(OWNER_DEFAULT_PAGE_KEYS)
  switch (c.role) {
    case 'owner': return withArchives(OWNER_DEFAULT_PAGE_KEYS)
    case 'admin': return explicit.length ? withArchives(explicit) : withArchives(ADMIN_DEFAULT_PAGE_KEYS)
    case 'online-team': return explicit.length ? withArchives(explicit.filter((key) => key !== 'profiles')) : withArchives(ONLINE_DEFAULT_PAGE_KEYS)
    case 'store': return withArchives(STORE_DEFAULT_PAGE_KEYS)
    case 'driver': return withArchives(DRIVER_DEFAULT_PAGE_KEYS)
    default:
      if (c.canManageOnTimeUsers) return withArchives(Array.from(new Set(['profiles', ...explicit])))
      return withArchives(explicit.filter((key) => key !== 'profiles'))
  }
}

export function pathAllowedForOnTime(claims, pathname = '') {
  const pageKey = getRequestedOnTimePageKey(pathname)
  return getAllowedOnTimePageKeys(claims).includes(pageKey)
}

export function getVisibleOnTimeTabs(claims) {
  const allowed = new Set(getAllowedOnTimePageKeys(claims))
  return ONTIME_PAGE_DEFS.filter((page) => allowed.has(page.key))
}

export function getOnTimeLandingPath(claims) {
  const c = normalizeModuleClaims(claims)
  if (!hasOnTimeAccess(c)) return '/no-access'
  const allowed = getAllowedOnTimePageKeys(c)
  const first = ONTIME_PAGE_DEFS.find((page) => allowed.includes(page.key))
  return first?.to || '/no-access'
}
